import { useEffect } from 'react';

import produce from 'immer';
import { orderBy } from 'lodash';
import qs from 'qs';
import { useWatch } from 'react-hook-form';
import { useQueryClient } from 'react-query';
import { useHistory, useLocation } from 'react-router-dom';
import { sortAlphabeticalArray } from 'utilitys/helpers/general';

import { useQueryState } from 'hooks/useQueryState';

import { useBattleCardsStore } from 'store/battlecards.store';
import { useProjectTreeStore } from 'store/projectContract.store';

import { getWBSs as getSingleWbs } from 'api/endPoints/getWBSs';
import { QueryResponse } from 'api/queries/getWbs.query';
import { QueryKeys } from 'api/queryKeys';

import {
    FinalData,
    IFields,
    IFieldsOptions,
} from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/MainFilters/MainFilters.types';
import {
    CustomizeFilterOptions,
    activityCodeMapperUID,
    buildDataForQueryString,
    buildFilterPayload,
    buildPayloadByKey,
    defaultLagLeadValues,
    defaultListOptionsForTrueFalse,
    getFlags,
    mapper,
    milestoneFragnetMapper,
    riskIndicatorsList,
    statusListOptions,
} from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/MainFilters/MainFilters.utils';
import { buildRowsBoardFilter } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/RowsBoard/RowsBoardColumns.utils';
import { assigneeMapper } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/helper';

export const useMainFiltersLogic = ({ api, ui }) => {
    const { project, contract } = useProjectTreeStore();
    const { setFilterData, setWidgetFiltersData } = useBattleCardsStore();
    const queryClient = useQueryClient();
    const history = useHistory();
    const { search } = useLocation();
    const formValues = useWatch({ control: ui.form.control });

    // Query state hooks
    const [qActivityFragnet] = useQueryState('activityFragnet');
    const [qActivityForwardFragnet] = useQueryState('activityForwardFragnet');
    const [qCardActivity] = useQueryState('cardActivity');
    const [qAssignee] = useQueryState('assignee');
    const [qStatus] = useQueryState('status');
    const [qRiskIndicators] = useQueryState('riskIndicators');
    const [qImportant] = useQueryState('important');
    const [qUrgent] = useQueryState('urgent');
    const [qIsAdvancedTextSearch] = useQueryState('isAdvancedTextSearch');
    const [qEndDatesRange] = useQueryState('endDatesRange');
    const [qStartDatesRange] = useQueryState('startDatesRange');
    const [qCommentRange] = useQueryState('commentRange');
    const [qBaselineStartDatesRange] = useQueryState('baselineStartDatesRange');
    const [qBaselineEndDatesRange] = useQueryState('baselineEndDatesRange');
    const [qFinishDate] = useQueryState('finishDate');
    const [qActivityCodes] = useQueryState('activityCodes');
    const [qExcludeActivityCodes] = useQueryState('excludeActivityCodes');
    const [qWbs] = useQueryState('wbs');
    const [qCardType] = useQueryState('cardType');
    const [qCompletionPlanSegment] = useQueryState('completionPlanSegment');
    const [qDuration] = useQueryState('duration');
    const [qFloat] = useQueryState('float');
    const [qHasAttachments] = useQueryState('hasAttachments');
    const [qHasComments] = useQueryState('hasComments');
    const [qLagLead] = useQueryState('lagLead');
    const [qRelatedActivity] = useQueryState('relatedActivity');
    const [qResourceType] = useQueryState('resourceType');
    const [qStartDate] = useQueryState('startDate');
    const [qSubmittedCompletion] = useQueryState('submittedCompletion');
    const [qTrackedActivity] = useQueryState('trackedActivity');
    const [qUpdatedBy] = useQueryState('updatedBy');
    const [qCommenter] = useQueryState('commenter');
    const [qMentioned] = useQueryState('mentioned');
    const [qWbsLevel] = useQueryState('wbsLevel');
    const [qResourceName] = useQueryState('resourceName');
    const [qBaselineStartDate] = useQueryState('baselineStartDate');
    const [qBaselineEndDate] = useQueryState('baselineEndDate');
    const [qCluster] = useQueryState('cluster');

    const fieldsOptions: IFieldsOptions = {
        typeCardOptions: api.boardInit.data?.cardType.map(mapper) ?? [],
        activityFragnetOptions:
            api.activityFragnetSingle.data ?? api.boardInit.data?.fragnetMilestone.map(milestoneFragnetMapper) ?? [],
        completionPlanSegmentOptions: api.boardInit.data?.fragnetCompletion.map(mapper) ?? [],
        cardActivityTypeOptions: api.boardInit.data?.cardActivityType.map(mapper) ?? [],
        assigneeOptions: api.assigneeOptions.options,
        updatedByOptions: api.boardInit.data?.assignee
            ? sortAlphabeticalArray(api.boardInit.data?.assignee.map(assigneeMapper), 'label', 'asc')
            : [],
        tagsOptions:
            api.boardInit.data?.tags.map((item) => ({
                label: item.name,
                value: { name: item.name, isExclude: false },
            })) ?? [],
        activityCodesOptions: orderBy(
            api.boardInit.data?.activityCode.map(activityCodeMapperUID) ?? [],
            [(item: any) => item['label'].toLowerCase()],
            ['asc'],
        ),
        trackedActivityOptions: defaultListOptionsForTrueFalse,
        hasAttachmentsOptions: defaultListOptionsForTrueFalse,
        lagLeadOptions: defaultLagLeadValues,
        importantOptions: api.boardInit.data?.cardImportant.map(mapper) ?? [],
        cardUrgentOptions: api.boardInit.data?.cardUrgent.map(mapper) ?? [],
        statusOptions: statusListOptions,
        resourceTypeOptions: api.boardInit.data?.resourceType.map(mapper) ?? [],
        riskIndicatorsOptions: riskIndicatorsList,
        resourceNameOptions:
            api.boardInit.data?.cardResource.map((i) => ({
                ...i,
                label: `${i.label} (~${i.count} related activities)`,
            })) ?? [],
        clusters: api.boardInit.data?.clusters ?? [],
    };

    const removeUnSelectedFiltersFromFormData = (data: IFields): FinalData => {
        return produce(data, (draft) => {
            for (const property in ui.state.customizeFilterOptions) {
                if (
                    ui.state.customizeFilterOptions.hasOwnProperty(property) &&
                    !ui.state.customizeFilterOptions[property].isSelected
                ) {
                    delete draft[property];
                }
            }
            return draft;
        });
    };

    const updateBattlecardsStore = async (formData) => {
        const formDataWithoutRemovedFilters = removeUnSelectedFiltersFromFormData(formData);
        const payload = buildFilterPayload(formDataWithoutRemovedFilters);

        setFilterData(payload);
        const params = { latestVersion: api.latestVersionId, taskId: formData.wbs };

        const singleWbs = await queryClient.fetchQuery<
            QueryResponse[],
            QueryError,
            QueryResponse[],
            [QueryKeys, unknown]
        >(['getSingleWbs', params], () => getSingleWbs(params));

        // Set Widget Filters Data to store
        const widgetFiltersData = buildRowsBoardFilter({
            filter: { filters: payload },
            activityCodes: api.boardInit.data?.activityCode,
            activityFragnet: api.boardInit.data?.fragnetMilestone,
            assigneeOptionsList: api.boardInit.data?.assignee,
            singleWbs: singleWbs,
        });

        setWidgetFiltersData(widgetFiltersData);
    };

    const onFiltersChanged = async (addedFilters) => {
        const formDataWithoutRemovedFilters = removeUnSelectedFiltersFromFormData({
            ...formValues,
            ...addedFilters,
        });
        const queryStringData = buildDataForQueryString(formDataWithoutRemovedFilters);
        const existingQueries = qs.parse(window.location.search, {
            ignoreQueryPrefix: true,
        });

        const queryString = qs.stringify(
            { ...existingQueries, ...queryStringData },
            { arrayFormat: 'comma', skipNulls: true, encode: true },
        );
        history.replace({
            search: queryString,
        });
    };

    const isActivityFragnettSingleDataReady =
        (!qActivityFragnet && api.latestVersionId) || (api.latestVersionId && api.activityFragnetSingle.data);

    const isActivityForwardFragnettSingleDataReady =
        (!qActivityForwardFragnet && api.latestVersionId) ||
        (api.latestVersionId && api.activityForwardFragnetSingle.data);

    // Map of query key names to their query state values for dynamic checking
    const queryStateMap: Record<string, unknown> = {
        activityFragnet: qActivityFragnet,
        activityForwardFragnet: qActivityForwardFragnet,
        cardActivity: qCardActivity,
        assignee: qAssignee,
        status: qStatus,
        riskIndicators: qRiskIndicators,
        important: qImportant,
        urgent: qUrgent,
        isAdvancedTextSearch: qIsAdvancedTextSearch,
        endDatesRange: qEndDatesRange,
        startDatesRange: qStartDatesRange,
        commentRange: qCommentRange,
        baselineStartDatesRange: qBaselineStartDatesRange,
        baselineEndDatesRange: qBaselineEndDatesRange,
        finishDate: qFinishDate,
        activityCodes: qActivityCodes,
        excludeActivityCodes: qExcludeActivityCodes,
        wbs: qWbs,
        cardType: qCardType,
        completionPlanSegment: qCompletionPlanSegment,
        duration: qDuration,
        float: qFloat,
        hasAttachments: qHasAttachments,
        hasComments: qHasComments,
        lagLead: qLagLead,
        relatedActivity: qRelatedActivity,
        resourceType: qResourceType,
        startDate: qStartDate,
        submittedCompletion: qSubmittedCompletion,
        trackedActivity: qTrackedActivity,
        updatedBy: qUpdatedBy,
        commenter: qCommenter,
        mentioned: qMentioned,
        wbsLevel: qWbsLevel,
        resourceName: qResourceName,
        baselineStartDate: qBaselineStartDate,
        baselineEndDate: qBaselineEndDate,
        cluster: qCluster,
    };

    /**
     * set initial query params if there is no query params in the url
     */
    useEffect(() => {
        search.length === 0 &&
            history.replace({
                search: '?cardActivity=TASK_DEPENDENT,START_MILESTONE',
            });
    }, []);

    /**
     * Initialize customize filter options based on query params
     * Determines if there are any query parameters present in the URL that correspond to the keys in the CustomizeFilterOptions object.
     * If a match is found, the respective filter option's isSelected property is set to true and the filter will be visible to the user.
     * Finally, it marks the customization options as initialized to prevent redundant executions.
     */
    useEffect(() => {
        ui.setters.setCustomizeFilterOptions(
            produce(CustomizeFilterOptions, (draft) => {
                Object.keys(draft).forEach((item) => {
                    if (queryStateMap[item]) {
                        draft[item].isSelected = true;
                    }
                });
                if (qStartDatesRange) {
                    draft.startDate.isSelected = true;
                }

                if (qBaselineStartDatesRange) {
                    draft.baselineStartDate.isSelected = true;
                }
                if (qBaselineEndDatesRange) {
                    draft.baselineEndDate.isSelected = true;
                }

                if (qCommentRange) {
                    draft.hasComments.isSelected = true;
                }
            }),
        );
        ui.setters.setIsCustomizeFilterOptionsInitialized(true);
    }, []);

    /**
     * update form from url params
     */
    useEffect(() => {
        if (!ui.state.isCustomizeFilterOptionsInitialized || !api.boardInit.data) {
            return;
        }
        if (isActivityFragnettSingleDataReady || isActivityForwardFragnettSingleDataReady) {
            ui.form.reset({
                project: project?.id,
                contract: contract?.id,
                version: api.latestVersionId,
                cardActivity: buildPayloadByKey.cardActivity.getValueByQuery({
                    value: (qCardActivity as string)?.split(','),
                    options: fieldsOptions.cardActivityTypeOptions,
                }),
                assignee: buildPayloadByKey.assignee.getValueByQuery({
                    value: qAssignee as string,
                    options: fieldsOptions.assigneeOptions,
                }),
                status: buildPayloadByKey.status.getValueByQuery({
                    value: (qStatus as string)?.split(','),
                    options: fieldsOptions.statusOptions,
                }),
                riskIndicators: buildPayloadByKey.riskIndicators.getValueByQuery({
                    value: (qRiskIndicators as string)?.split(','),
                    options: fieldsOptions.riskIndicatorsOptions,
                }),
                important: buildPayloadByKey.important.getValueByQuery({
                    value: qImportant as string,
                    options: fieldsOptions.importantOptions,
                }),
                urgent: buildPayloadByKey.urgent.getValueByQuery({
                    value: qUrgent as string,
                    options: fieldsOptions.cardUrgentOptions,
                }),
                tags: buildPayloadByKey.tags.getValueByQuery({
                    tagsValue: qs.parse(window.location.search, { ignoreQueryPrefix: true })['tags'],
                    excludeTagsValue: qs.parse(window.location.search, { ignoreQueryPrefix: true })['excludeTags'],
                    options: fieldsOptions.tagsOptions,
                }),
                cardSearch:
                    (qs.parse(window.location.search, {
                        ignoreQueryPrefix: true,
                    }).cardSearch as string) || '',

                flags: getFlags({
                    isAdvancedTextSearch: qIsAdvancedTextSearch === 'true',
                    endDatesRange: qEndDatesRange as string,
                    startDatesRange: qStartDatesRange as string,
                    commentRange: qCommentRange as string,
                    baselineStartDatesRange: qBaselineStartDatesRange as string,
                    baselineEndDatesRange: qBaselineEndDatesRange as string,
                }),

                finishDate: buildPayloadByKey.finishDate.getValueByQuery({
                    value: qFinishDate as string,
                    flagValue: qEndDatesRange as string,
                }),

                activityCodes: buildPayloadByKey.activityCodes.getValueByQuery({
                    activityCodesValue: (qActivityCodes as string)?.split(','),
                    excludeActivityCodesValue: (qExcludeActivityCodes as string)?.split(','),
                    options: fieldsOptions.activityCodesOptions,
                }),

                wbs: (qWbs as string) || '',

                // Optional
                activityFragnet: api.activityFragnetSingle.data
                    ? api.activityFragnetSingle.data.map(milestoneFragnetMapper).find((item) => {
                          return item.value === qActivityFragnet;
                      })
                    : null,

                activityForwardFragnet: api.activityForwardFragnetSingle.data
                    ? api.activityForwardFragnetSingle.data
                          .map(milestoneFragnetMapper)
                          .find((item) => item.value === qActivityForwardFragnet)
                    : null,

                cardType: buildPayloadByKey.cardType.getValueByQuery({
                    value: (qCardType as string)?.split(','),
                    options: fieldsOptions.typeCardOptions,
                }),

                completionPlanSegment: buildPayloadByKey.completionPlanSegment.getValueByQuery({
                    value: qCompletionPlanSegment as string,
                    options: fieldsOptions.completionPlanSegmentOptions,
                }),

                duration: (qDuration as string)?.split(',').map(Number) || null,

                float: (qFloat as string)?.split(',').map(Number) || null,

                hasAttachments: buildPayloadByKey.hasAttachments.getValueByQuery({
                    value: qHasAttachments as string,
                    options: fieldsOptions.hasAttachmentsOptions,
                }),

                hasComments: buildPayloadByKey.hasComments.getValueByQuery({
                    value: qHasComments as string,
                    flagValue: qCommentRange as string,
                }),

                lagLead: buildPayloadByKey.lagLead.getValueByQuery({
                    value: qLagLead as string,
                    options: fieldsOptions.lagLeadOptions,
                }),

                relatedActivity: (qRelatedActivity as string) || '',

                resourceType: buildPayloadByKey.resourceType.getValueByQuery({
                    value: (qResourceType as string)?.split(','),
                    options: fieldsOptions.resourceTypeOptions,
                }),

                startDate: buildPayloadByKey.startDate.getValueByQuery({
                    value: qStartDate as string,
                    flagValue: qStartDatesRange as string,
                }),

                submittedCompletion: (qSubmittedCompletion as string)?.split(',').map(Number) || [0, 100],

                trackedActivity: buildPayloadByKey.trackedActivity.getValueByQuery({
                    value: qTrackedActivity as string,
                    options: fieldsOptions.trackedActivityOptions,
                }),

                updatedBy: buildPayloadByKey.updatedBy.getValueByQuery({
                    value: qUpdatedBy as string,
                    options: fieldsOptions.updatedByOptions,
                }),

                commenter: buildPayloadByKey.commenter.getValueByQuery({
                    value: qCommenter as string,
                    options: fieldsOptions.updatedByOptions,
                }),

                mentioned: buildPayloadByKey.mentioned.getValueByQuery({
                    value: qMentioned as string,
                    options: fieldsOptions.updatedByOptions,
                }),

                wbsLevel: (qWbsLevel as string)?.split(',').map(Number) || [0, 10],

                resourceName: buildPayloadByKey.resourceName.getValueByQuery({
                    value: (qResourceName as string)?.split(','),
                    options: fieldsOptions.resourceNameOptions,
                }),

                baselineStartDate: buildPayloadByKey.baselineStartDate.getValueByQuery({
                    value: qBaselineStartDate as string,
                    flagValue: qBaselineStartDatesRange as string,
                }),

                baselineEndDate: buildPayloadByKey.baselineEndDate.getValueByQuery({
                    value: qBaselineEndDate as string,
                    flagValue: qBaselineEndDatesRange as string,
                }),

                cluster: buildPayloadByKey.cluster.getValueByQuery({
                    value: qCluster as string,
                    options: fieldsOptions.clusters,
                }),
            });
            ui.form.handleSubmit(updateBattlecardsStore)();
        }
    }, [
        api.boardInit.data,
        api.latestVersionId,
        api.activityFragnetSingle.data,
        api.activityForwardFragnetSingle.data,
        ui.state.customizeFilterOptions,
        ui.state.isCustomizeFilterOptionsInitialized,
        search,
    ]);

    return {
        logic: { fieldsOptions, onFiltersChanged },
    };
};
